import { initDocumentPreviewPolling, teardownDocumentPreviewPolling } from "./components/document_preview_polling";
import { initTaskDelivery, teardownTaskDelivery } from "./components/task_delivery";
import { initTaskWorkspace, teardownTaskWorkspace } from "./components/task_workspace";
import { mountSwpBugReporter } from "./components/bug_reporter";
import { initOrderSlipLabLines } from "./components/order_slip_lab_lines";
import DOMPurify from "dompurify";
import React from "react";
import { createRoot } from "react-dom/client";
import { marked } from "marked";
import PatientPacketWorkspace from "./components/patient_packet_workspace";
import BookingSheetWorkspace from "./components/booking_sheet_workspace";
import ConsultNoteWorkspace from "./components/consult_note_workspace";
import ConsultSchedulingInviteWorkspace from "./components/staff_scheduling_invites/consult_scheduling_invite_workspace";
import DocumentAiWorkspace from "./components/document_ai_workspace";
import DesktopPatientSearch from "./components/desktop_patient_search";
import FaxExtractionPrompt from "./components/fax_extraction_prompt";
import FaxPatientAssignment from "./components/fax_patient_assignment";
import FaceSheetValues from "./components/face_sheet_values";
import FaceSheetWorkspace from "./components/face_sheet_workspace";
import OrderSlipCalibration from "./components/order_slip_calibration";
import OperationSchedulingInviteWorkspace from "./components/staff_scheduling_invites/operation_scheduling_invite_workspace";
import PatientDetailsEditor from "./components/patient_details_editor";
import PatientPicker from "./components/patient_picker";
import PatientMergeWorkspace from "./components/patient_merge_workspace";

const THEME_STORAGE_KEY = "swp-theme";
const BROWSER_TIME_ZONE_COOKIE = "browser_time_zone";
let teardownLabResultGraphTooltips = null;
const OPEN_IN_WINDOW_FEATURES = "popup=yes,width=1280,height=900,resizable=yes,scrollbars=yes,noopener,noreferrer";

function syncBrowserTimeZoneCookie() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timeZone) return;

    const encodedTimeZone = encodeURIComponent(timeZone);
    const existingTimeZone = document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith(`${BROWSER_TIME_ZONE_COOKIE}=`))
      ?.split("=")[1];

    if (existingTimeZone === encodedTimeZone) return;

    document.cookie = `${BROWSER_TIME_ZONE_COOKIE}=${encodedTimeZone}; path=/; max-age=31536000; samesite=lax`;
  } catch (_error) {
    // Ignore browser time zone sync failures and keep the current server zone.
  }
}

function readStoredTheme() {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch (_error) {
    return null;
  }
}

function writeStoredTheme(theme) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (_error) {
    // Ignore storage failures and still apply the theme for the current session.
  }
}

function preferredDarkMode() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function currentTheme() {
  const savedTheme = readStoredTheme();
  if (savedTheme === "dark" || savedTheme === "light") return savedTheme;

  return preferredDarkMode() ? "dark" : "light";
}

function applyTheme(theme) {
  const darkMode = theme === "dark";
  document.documentElement.classList.toggle("dark", darkMode);
  writeStoredTheme(theme);
}

function initThemeToggle() {
  const buttons = document.querySelectorAll("[data-theme-toggle]");
  if (buttons.length === 0) return;

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      applyTheme(document.documentElement.classList.contains("dark") ? "light" : "dark");
    });
  });
}

function initMobileSidebar() {
  const sidebar = document.querySelector("[data-app-sidebar]");
  const overlay = document.querySelector("[data-app-overlay]");
  const openButtons = document.querySelectorAll("[data-sidebar-open]");
  const closeButtons = document.querySelectorAll("[data-sidebar-close]");
  const navLinks = document.querySelectorAll("[data-sidebar-nav-link]");
  const menuIcons = document.querySelectorAll("[data-menu-icon]");
  const closeIcons = document.querySelectorAll("[data-close-icon]");

  if (!sidebar || !overlay) return;

  let menuOpen = false;
  const desktopMedia = window.matchMedia("(min-width: 768px)");

  const syncMenu = () => {
    sidebar.classList.toggle("-translate-x-full", !menuOpen);
    overlay.classList.toggle("hidden", !menuOpen);
    document.body.classList.toggle("overflow-hidden", menuOpen && !desktopMedia.matches);

    openButtons.forEach((button) => {
      button.setAttribute("aria-expanded", menuOpen.toString());
    });

    menuIcons.forEach((icon) => icon.classList.toggle("hidden", menuOpen));
    closeIcons.forEach((icon) => icon.classList.toggle("hidden", !menuOpen));
  };

  const setMenuOpen = (value) => {
    menuOpen = value && !desktopMedia.matches;
    syncMenu();
  };

  openButtons.forEach((button) => {
    button.addEventListener("click", () => setMenuOpen(!menuOpen));
  });

  closeButtons.forEach((button) => {
    button.addEventListener("click", () => setMenuOpen(false));
  });

  overlay.addEventListener("click", () => setMenuOpen(false));
  navLinks.forEach((link) => link.addEventListener("click", () => setMenuOpen(false)));
  desktopMedia.addEventListener("change", () => setMenuOpen(false));

  syncMenu();
}

function initAdvancedSearchToggles() {
  const buttons = document.querySelectorAll("[data-advanced-search-toggle]");
  if (buttons.length === 0) return;

  buttons.forEach((button) => {
    const panelId = button.getAttribute("aria-controls");
    const panel = panelId ? document.getElementById(panelId) : null;
    const icon = button.querySelector("[data-advanced-search-icon]");
    if (!panel) return;

    const syncState = (expanded) => {
      panel.classList.toggle("hidden", !expanded);
      button.setAttribute("aria-expanded", expanded.toString());
      const label = expanded ? button.dataset.expandedLabel : button.dataset.collapsedLabel;
      button.setAttribute("aria-label", label);
      button.setAttribute("title", label);
      icon?.classList.toggle("rotate-180", expanded);
    };

    syncState(button.getAttribute("aria-expanded") === "true");
    button.addEventListener("click", () => {
      syncState(button.getAttribute("aria-expanded") !== "true");
    });
  });
}

function initOpenInWindowLinks() {
  if (document.documentElement.dataset.openInWindowBound === "true") return;

  document.documentElement.dataset.openInWindowBound = "true";

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest("a[data-open-in-window='true']");
    if (!(link instanceof HTMLAnchorElement) || !link.href) return;

    const openedWindow = window.open(
      link.href,
      "_blank",
      link.dataset.windowFeatures || OPEN_IN_WINDOW_FEATURES,
    );
    if (!openedWindow) return;

    try {
      openedWindow.opener = null;
    } catch (_error) {
      // Ignore cross-origin opener access failures.
    }

    openedWindow.focus();
    event.preventDefault();
  });
}

function dismissibleDetailsElements() {
  return Array.from(document.querySelectorAll("details[data-dismissible-details='true']")).filter(
    (element) => element instanceof HTMLDetailsElement,
  );
}

function closestDismissibleDetails(target) {
  if (!(target instanceof Node)) return null;

  const element = target instanceof Element ? target : target.parentElement;
  if (!element) return null;

  const details = element.closest("details[data-dismissible-details='true']");
  return details instanceof HTMLDetailsElement ? details : null;
}

function closeDismissibleDetails(except = null) {
  dismissibleDetailsElements().forEach((details) => {
    if (!details.open || details === except) return;

    details.removeAttribute("open");
  });
}

function initDismissibleDetails() {
  if (document.documentElement.dataset.dismissibleDetailsBound === "true") return;

  document.documentElement.dataset.dismissibleDetailsBound = "true";

  document.addEventListener("click", (event) => {
    closeDismissibleDetails(closestDismissibleDetails(event.target));
  });

  document.addEventListener("focusin", (event) => {
    closeDismissibleDetails(closestDismissibleDetails(event.target));
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    const details = closestDismissibleDetails(event.target);
    if (!details?.open) return;

    details.removeAttribute("open");
    details.querySelector("summary")?.focus();
  });
}

function initDebouncedAutoSubmitForms() {
  const forms = document.querySelectorAll("form[data-debounced-auto-submit='true']");
  if (forms.length === 0) return;

  forms.forEach((form) => {
    if (form.dataset.debouncedAutoSubmitBound === "true") return;

    const delay = Number.parseInt(form.dataset.debouncedAutoSubmitDelay || "300", 10);
    const targetId = form.dataset.debouncedAutoSubmitTarget;
    const inputs = Array.from(form.querySelectorAll("[data-debounced-auto-submit-input='true']"));
    const clearPendingSubmit = () => {
      if (!form.__debouncedAutoSubmitTimeoutId) return;

      window.clearTimeout(form.__debouncedAutoSubmitTimeoutId);
      form.__debouncedAutoSubmitTimeoutId = null;
    };

    const buildRequestUrl = (submitter = null) => {
      const requestUrl = new URL(form.action, window.location.origin);
      const params = new URLSearchParams(new FormData(form));

      if (
        submitter instanceof HTMLButtonElement ||
        submitter instanceof HTMLInputElement
      ) {
        if (submitter.name) {
          params.append(submitter.name, submitter.value);
        }
      }

      requestUrl.search = params.toString();
      return requestUrl;
    };

    const renderTargetFromResponse = async (submitter = null) => {
      const requestUrl = buildRequestUrl(submitter);

      if (!targetId) {
        window.location.assign(requestUrl);
        return;
      }

      const target = document.getElementById(targetId);
      if (!target) {
        window.location.assign(requestUrl);
        return;
      }

      form.__debouncedAutoSubmitAbortController?.abort();
      const controller = new AbortController();
      form.__debouncedAutoSubmitAbortController = controller;
      target.setAttribute("aria-busy", "true");

      try {
        const response = await fetch(requestUrl, {
          headers: {
            Accept: "text/html",
            "X-Patients-Index-Partial": "results",
            "X-Requested-With": "XMLHttpRequest",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, "text/html");
        const nextTarget = documentFragment.getElementById(targetId);
        if (!nextTarget) {
          throw new Error(`Missing replacement target: ${targetId}`);
        }

        target.replaceWith(nextTarget);
        window.history.replaceState({}, "", requestUrl);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;

        window.location.assign(requestUrl);
      } finally {
        if (form.__debouncedAutoSubmitAbortController === controller) {
          form.__debouncedAutoSubmitAbortController = null;
        }

        target.removeAttribute("aria-busy");
      }
    };

    const submit = () => {
      clearPendingSubmit();
      form.__debouncedAutoSubmitTimeoutId = window.setTimeout(() => {
        renderTargetFromResponse();
      }, Number.isNaN(delay) ? 300 : delay);
    };

    const handleSubmit = (event) => {
      clearPendingSubmit();
      if (!targetId) return;

      event.preventDefault();
      renderTargetFromResponse(event.submitter);
    };

    form.dataset.debouncedAutoSubmitBound = "true";
    form.__debouncedAutoSubmitCleanup = () => {
      clearPendingSubmit();
      form.__debouncedAutoSubmitAbortController?.abort();
      form.removeEventListener("submit", handleSubmit);

      inputs.forEach((input) => {
        input.removeEventListener("input", submit);
        input.removeEventListener("change", submit);
        input.removeEventListener("search", submit);
      });

      delete form.dataset.debouncedAutoSubmitBound;
      delete form.__debouncedAutoSubmitCleanup;
      delete form.__debouncedAutoSubmitAbortController;
      delete form.__debouncedAutoSubmitTimeoutId;
    };
    form.addEventListener("submit", handleSubmit);

    inputs.forEach((input) => {
      input.addEventListener("input", submit);
      input.addEventListener("change", submit);
      input.addEventListener("search", submit);
    });
  });
}

function initFlashDismissButtons() {
  const buttons = document.querySelectorAll("[data-flash-dismiss]");
  if (buttons.length === 0) return;

  buttons.forEach((button) => {
    if (button.dataset.flashDismissBound === "true") return;

    button.dataset.flashDismissBound = "true";
    button.addEventListener("click", () => {
      const flashMessage = button.closest("[data-flash-message]");
      if (!flashMessage) return;

      const flashContainer = flashMessage.closest("[data-flash-container]");
      flashMessage.remove();

      if (!flashContainer?.querySelector("[data-flash-message]")) {
        flashContainer.remove();
      }
    });
  });
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.className = "sr-only";
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  try {
    if (!document.execCommand("copy")) {
      throw new Error("Copy command failed");
    }
  } finally {
    textarea.remove();
  }
}

function resetCopyButtonLabel(button, label) {
  window.setTimeout(() => {
    button.textContent = label;
  }, 1500);
}

function initCopyButtons() {
  const buttons = document.querySelectorAll("[data-copy-text]");
  if (buttons.length === 0) return;

  buttons.forEach((button) => {
    if (button.dataset.copyBound === "true") return;

    button.dataset.copyBound = "true";
    button.addEventListener("click", async () => {
      const text = button.dataset.copyText;
      if (!text) return;

      const originalLabel = button.dataset.copyLabel || button.textContent;

      try {
        await copyTextToClipboard(text);
        button.textContent = "Copied";
        resetCopyButtonLabel(button, originalLabel);
      } catch (_error) {
        button.textContent = "Copy failed";
        resetCopyButtonLabel(button, originalLabel);
      }
    });
  });
}

function normalizeOperationNoteCopyText(value) {
  return value.toString().replace(/\r\n?/g, "\n").trim();
}

function operationNoteFieldValue(field) {
  if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
    return field.value;
  }

  return field?.textContent || "";
}

function buildOperationNoteCopyText(form) {
  const title = normalizeOperationNoteCopyText(operationNoteFieldValue(form.querySelector("[data-operation-note-title-input]")));
  const sections = Array.from(form.querySelectorAll("[data-operation-note-section]"));
  const blocks = [
    title,
    ...sections.map((section) => {
      const sectionTitle = normalizeOperationNoteCopyText(section.querySelector("[data-operation-note-section-title]")?.textContent || "");
      const sectionText = normalizeOperationNoteCopyText(operationNoteFieldValue(section.querySelector("[data-operation-note-section-input]")));
      return [ sectionTitle ? sectionTitle.toUpperCase() : null, sectionText ].filter(Boolean).join("\n");
    })
  ].filter(Boolean);

  return blocks.join("\n\n").trim();
}

function initOperationNoteCopyButtons() {
  const buttons = document.querySelectorAll("[data-operation-note-copy-button]");
  if (buttons.length === 0) return;

  buttons.forEach((button) => {
    if (button.dataset.operationNoteCopyBound === "true") return;

    button.dataset.operationNoteCopyBound = "true";
    button.addEventListener("click", async () => {
      const formId = button.dataset.operationNoteFormId;
      const form = formId ? document.getElementById(formId) : null;
      if (!(form instanceof HTMLFormElement)) return;

      const text = buildOperationNoteCopyText(form);
      if (!text) return;

      const originalLabel = button.dataset.copyLabel || button.textContent;

      try {
        await copyTextToClipboard(text);
        button.textContent = "Copied";
        resetCopyButtonLabel(button, originalLabel);
      } catch (_error) {
        button.textContent = "Copy failed";
        resetCopyButtonLabel(button, originalLabel);
      }
    });
  });
}

function initTaskStatusForms() {
  if (document.documentElement.dataset.taskStatusFormsBound === "true") return;

  document.documentElement.dataset.taskStatusFormsBound = "true";

  document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.taskStatusForm !== "true") return;

    event.preventDefault();

    const row = form.closest("[data-task-row]");
    const list = form.closest("[data-task-list]");
    const actionsMenu = form.closest("[data-task-actions-menu]");
    const submitButton = event.submitter instanceof HTMLButtonElement ? event.submitter : form.querySelector("button");
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

    if (!(row instanceof HTMLElement) || !(list instanceof HTMLElement)) {
      form.submit();
      return;
    }

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.classList.add("opacity-60", "cursor-not-allowed");
    }

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: new FormData(form),
        credentials: "same-origin",
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(Array.isArray(payload.errors) ? payload.errors.join(", ") : "Unable to update task status.");
      }

      const task = payload.task || {};
      if (task.remove_from_list) {
        row.remove();

        const remainingRows = list.querySelectorAll("[data-task-row]");
        if (remainingRows.length === 0) {
          const emptyMessage = list.dataset.taskListEmptyMessage || "No tasks to show.";
          list.innerHTML =
            `<p class="rounded-md border border-dashed border-neutral-300 px-3 py-4 text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">${emptyMessage}</p>`;
        }

        return;
      }

      const statusBadge = row.querySelector("[data-task-status-badge]");
      if (statusBadge instanceof HTMLElement) {
        statusBadge.className = task.status_badge_classes || statusBadge.className;
        statusBadge.textContent = task.status_label || statusBadge.textContent;
      }

      const overdueBadge = row.querySelector("[data-task-overdue-badge]");
      if (overdueBadge instanceof HTMLElement) {
        overdueBadge.classList.toggle("hidden", !task.overdue);
      }

      row.dataset.taskStatus = task.status || "";
      if (actionsMenu instanceof HTMLDetailsElement) {
        actionsMenu.open = false;
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Unable to update task status.");
    } finally {
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
        submitButton.classList.remove("opacity-60", "cursor-not-allowed");
      }
    }
  });
}

function initSlotTypeFilters() {
  const groups = document.querySelectorAll("[data-slot-type-filter]");
  if (groups.length === 0) return;

  groups.forEach((group) => {
    const inputs = group.querySelectorAll("[data-slot_type_filter_input], [data-slot-type-filter-input='true']");
    const target = document.querySelector("[data-slot-type-filter-target]");
    const telehealthFormatPanel = document.querySelector("[data-telehealth-format-panel]");
    if (inputs.length === 0 || !target) return;

    let defaultSelectedSlotKeysByFilter = {};
    try {
      defaultSelectedSlotKeysByFilter = JSON.parse(target.dataset.defaultSelectedSlotKeysByFilter || "{}") || {};
    } catch (_error) {
      defaultSelectedSlotKeysByFilter = {};
    }

    let defaultSlotSelectionActive = target.dataset.defaultSlotSelectionActive === "true";

    const selectionInputs = () =>
      target.querySelectorAll(
        "input[name='consult_scheduling_invite[selected_slot_keys][]']",
      );

    const markSelectionEdited = (event) => {
      if (target.dataset.applyingDefaultSlotSelection === "true") return;
      if (!(event.target instanceof HTMLInputElement)) return;
      if (!Array.from(selectionInputs()).includes(event.target)) return;

      defaultSlotSelectionActive = false;
      target.dataset.defaultSlotSelectionActive = "false";
    };

    if (target.dataset.defaultSlotSelectionBound !== "true") {
      target.dataset.defaultSlotSelectionBound = "true";
      target.addEventListener("change", markSelectionEdited);
    }

    const applyDefaultSlotSelection = (filterValue) => {
      if (!defaultSlotSelectionActive) return;

      const defaultSlotKeys = new Set(defaultSelectedSlotKeysByFilter[filterValue] || []);
      target.dataset.applyingDefaultSlotSelection = "true";

      try {
        selectionInputs().forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;

          input.checked = defaultSlotKeys.has(input.value);
        });
      } finally {
        delete target.dataset.applyingDefaultSlotSelection;
      }
    };

    const applyFilter = () => {
      const checkedInput = Array.from(inputs).find((input) => input.checked);
      const filterValue = checkedInput?.value || "all";
      const sections = target.querySelectorAll("[data-slot-type-filter-section]");

      applyDefaultSlotSelection(filterValue);

      if (telehealthFormatPanel instanceof HTMLElement) {
        telehealthFormatPanel.classList.toggle("hidden", filterValue === "office");
      }

      sections.forEach((section) => {
        const items = section.querySelectorAll("[data-slot-type-filter-item]");
        let visibleCount = 0;

        items.forEach((item) => {
          const matches = filterValue === "all" || item.dataset.slotLocation === filterValue;
          item.classList.toggle("hidden", !matches);
          const input = item.querySelector("input[type='checkbox']");
          if (input) input.disabled = !matches;
          if (matches) visibleCount += 1;
        });

        section.classList.toggle("hidden", visibleCount === 0);
      });
    };

    inputs.forEach((input) => {
      if (input.dataset.slotTypeFilterBound === "true") return;

      input.dataset.slotTypeFilterBound = "true";
      input.addEventListener("change", applyFilter);
    });

    applyFilter();
  });
}

function initSchedulingInviteRangeLinks() {
  const availabilitySectionSelector = [
    "[data-consult-schedule-availability-section]",
    "[data-operation-schedule-availability-section]",
  ].join(", ");
  const links = document.querySelectorAll("[data-consult-schedule-range-link='true'], [data-operation-schedule-range-link='true']");
  if (links.length === 0) return;

  links.forEach((link) => {
    if (link.dataset.consultScheduleRangeLinkBound === "true") return;

    link.dataset.consultScheduleRangeLinkBound = "true";
    link.addEventListener("click", async (event) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      const url = new URL(link.href, window.location.origin);
      const availabilitySection = link.closest(availabilitySectionSelector);
      const filterInputName =
        availabilitySection instanceof HTMLElement
          ? availabilitySection.dataset.scheduleRangeFilterInputName || "consult_scheduling_invite[slot_type_filter]"
          : "consult_scheduling_invite[slot_type_filter]";
      const slotTypeInput = document.querySelector(`input[name="${filterInputName}"]:checked`);

      if (slotTypeInput instanceof HTMLInputElement) {
        url.searchParams.set(filterInputName, slotTypeInput.value);
      }

      event.preventDefault();

      if (!(availabilitySection instanceof HTMLElement)) {
        window.location.assign(url);
        return;
      }
      if (availabilitySection.getAttribute("aria-busy") === "true") return;

      availabilitySection.setAttribute("aria-busy", "true");
      availabilitySection.classList.add("opacity-70");

      try {
        const response = await fetch(url, {
          credentials: "same-origin",
          headers: {
            Accept: "text/html",
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (!response.ok) throw new Error("Unable to load availability.");

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, "text/html");
        const nextAvailabilitySection = documentFragment.querySelector(availabilitySectionSelector);

        if (!(nextAvailabilitySection instanceof HTMLElement)) throw new Error("Unable to load availability.");

        availabilitySection.replaceWith(nextAvailabilitySection);
        initSlotTypeFilters();
        initSchedulingInviteRangeLinks();
        initSelectablePillGroups();
        nextAvailabilitySection.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (error) {
        availabilitySection.removeAttribute("aria-busy");
        availabilitySection.classList.remove("opacity-70");
        window.alert(error instanceof Error ? error.message : "Unable to load availability.");
      }
    });
  });
}

function initSelectablePillGroups() {
  const groups = document.querySelectorAll("[data-selectable-pill-group]");
  if (groups.length === 0) return;

  groups.forEach((group) => {
    const pills = group.querySelectorAll("[data-selectable-pill]");
    const inputs = group.querySelectorAll("input[type='radio'], input[type='checkbox']");
    if (pills.length === 0 || inputs.length === 0) return;

    const sync = () => {
      pills.forEach((pill) => {
        const input = pill.querySelector("input[type='radio'], input[type='checkbox']");
        const active = input instanceof HTMLInputElement ? input.checked : false;

        pill.classList.toggle("app-pill-active", active);
        pill.classList.toggle("app-pill-inactive", !active);
      });
    };

    inputs.forEach((input) => {
      if (input.dataset.selectablePillBound === "true") return;

      input.dataset.selectablePillBound = "true";
      input.addEventListener("change", sync);
    });

    sync();
  });
}

function confirmationMessage(element) {
  if (!(element instanceof HTMLElement)) return null;

  return element.dataset.turboConfirm || element.dataset.confirm || null;
}

function initConfirmationPrompts() {
  if (document.documentElement.dataset.confirmPromptsBound === "true") return;

  document.documentElement.dataset.confirmPromptsBound = "true";

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
      const message = confirmationMessage(submitter) || confirmationMessage(form);
      if (!message) return;

      if (!window.confirm(message)) {
        event.preventDefault();
      }
    },
    true
  );

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const link = target.closest("a[data-turbo-confirm], a[data-confirm]");
      if (!(link instanceof HTMLAnchorElement)) return;

      const message = confirmationMessage(link);
      if (!message) return;

      if (!window.confirm(message)) {
        event.preventDefault();
      }
    },
    true
  );
}

function initCalendarScrollForms() {
  const forms = document.querySelectorAll("[data-calendar-scroll-form]");
  if (forms.length === 0) return;

  forms.forEach((form) => {
    if (form.dataset.calendarScrollFormBound === "true") return;

    const dateInput = form.querySelector("input[name='date']");
    const anchorPrefix = form.dataset.calendarDayAnchorPrefix;
    if (!dateInput || !anchorPrefix) return;

    form.dataset.calendarScrollFormBound = "true";
    form.dataset.calendarScrollBaseAction = form.action.split("#")[0];

    form.addEventListener("submit", () => {
      const selectedDate = dateInput.value;
      const baseAction = form.dataset.calendarScrollBaseAction || form.action.split("#")[0];

      form.action = selectedDate ? `${baseAction}#${anchorPrefix}${selectedDate}` : baseAction;
    });
  });
}

function initCalendarDayToggles() {
  const buttons = document.querySelectorAll("[data-calendar-day-toggle]");
  if (buttons.length === 0) return;

  const syncState = (button, panel, expanded) => {
    const icon = button.querySelector("[data-calendar-day-toggle-icon]");
    const label = button.querySelector("[data-calendar-day-toggle-label]");

    panel.classList.toggle("hidden", !expanded);
    button.setAttribute("aria-expanded", expanded.toString());
    label.textContent = expanded ? button.dataset.expandedLabel : button.dataset.collapsedLabel;
    icon?.classList.toggle("rotate-180", expanded);
  };

  buttons.forEach((button) => {
    if (button.dataset.calendarDayToggleBound === "true") return;

    const panelId = button.getAttribute("aria-controls");
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;

    button.dataset.calendarDayToggleBound = "true";
    syncState(button, panel, button.getAttribute("aria-expanded") === "true");

    button.addEventListener("click", () => {
      syncState(button, panel, button.getAttribute("aria-expanded") !== "true");
    });
  });

  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return;

  const target = document.getElementById(hash);
  const toggle = target?.querySelector("[data-calendar-day-toggle]");
  if (!(toggle instanceof HTMLButtonElement)) return;

  const panelId = toggle.getAttribute("aria-controls");
  const panel = panelId ? document.getElementById(panelId) : null;
  if (!panel) return;

  syncState(toggle, panel, true);
}

function initExpandableText() {
  const buttons = document.querySelectorAll("[data-expandable-text-toggle]");
  if (buttons.length === 0) {
    initExpandableTextScopes();
    return;
  }

  buttons.forEach((button) => {
    if (button.dataset.expandableTextBound === "true") return;

    const container = button.closest("[data-expandable-text]");
    const collapsed = container?.querySelector("[data-expandable-text-collapsed]");
    const expanded = container?.querySelector("[data-expandable-text-expanded]");
    if (!container || !collapsed || !expanded) return;

    const syncState = (isExpanded) => {
      collapsed.classList.toggle("hidden", isExpanded);
      expanded.classList.toggle("hidden", !isExpanded);
      button.setAttribute("aria-expanded", isExpanded.toString());
      button.textContent = isExpanded ? button.dataset.expandedLabel : button.dataset.collapsedLabel;
      container.dataset.expandableTextExpanded = isExpanded.toString();
      container.dispatchEvent(
        new CustomEvent("expandable-text:change", {
          bubbles: true,
          detail: { expanded: isExpanded },
        }),
      );
    };

    button.dataset.expandableTextBound = "true";
    syncState(button.getAttribute("aria-expanded") === "true");
    button.addEventListener("click", () => {
      syncState(button.getAttribute("aria-expanded") !== "true");
    });
  });

  initExpandableTextScopes();
}

function initExpandableTextScopes() {
  const scopes = document.querySelectorAll("[data-expandable-text-scope]");
  if (scopes.length === 0) return;

  scopes.forEach((scope) => {
    const button = scope.querySelector("[data-expandable-text-toggle-all]");
    if (!(button instanceof HTMLButtonElement)) return;

    const toggleButtons = () =>
      Array.from(scope.querySelectorAll("[data-expandable-text-toggle]")).filter(
        (toggleButton) => toggleButton.closest("[data-expandable-text-scope]") === scope,
      );

    const syncScopeState = () => {
      const buttonsInScope = toggleButtons();
      const allExpanded =
        buttonsInScope.length > 0 &&
        buttonsInScope.every((toggleButton) => toggleButton.getAttribute("aria-expanded") === "true");

      button.disabled = buttonsInScope.length === 0;
      button.setAttribute("aria-expanded", allExpanded.toString());
      button.textContent = allExpanded ? button.dataset.expandedLabel : button.dataset.collapsedLabel;
      button.classList.toggle("opacity-60", button.disabled);
      button.classList.toggle("cursor-not-allowed", button.disabled);
    };

    if (button.dataset.expandableTextScopeBound !== "true") {
      button.dataset.expandableTextScopeBound = "true";
      button.addEventListener("click", () => {
        const buttonsInScope = toggleButtons();
        const expandAll = !buttonsInScope.every((toggleButton) => toggleButton.getAttribute("aria-expanded") === "true");

        buttonsInScope.forEach((toggleButton) => {
          const isExpanded = toggleButton.getAttribute("aria-expanded") === "true";
          if (isExpanded !== expandAll) toggleButton.click();
        });
      });

      scope.addEventListener("expandable-text:change", syncScopeState);
    }

    syncScopeState();
  });
}

function initEmailThreadOriginalToggles() {
  const buttons = document.querySelectorAll("[data-email-thread-original-toggle]");
  if (buttons.length === 0) return;

  buttons.forEach((button) => {
    if (button.dataset.emailThreadOriginalToggleBound === "true") return;

    const panelId = button.dataset.emailThreadOriginalTarget;
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;

    const syncState = (isExpanded) => {
      panel.classList.toggle("hidden", !isExpanded);
      button.setAttribute("aria-expanded", isExpanded.toString());
    };

    button.dataset.emailThreadOriginalToggleBound = "true";
    syncState(button.getAttribute("aria-expanded") === "true");
    button.addEventListener("click", () => {
      syncState(button.getAttribute("aria-expanded") !== "true");
    });
  });
}

function initDocumentAttachModals() {
  const dialogs = document.querySelectorAll("[data-document-attach-modal]");
  if (dialogs.length === 0) return;

  dialogs.forEach((dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;

    const form = dialog.querySelector("[data-document-attach-form]");
    const patientIdInput = dialog.querySelector("[data-document-attach-patient-id]");
    const returnToInput = dialog.querySelector("[data-document-attach-return-to]");
    const anchorInput = dialog.querySelector("[data-document-attach-anchor]");
    const kindInput = dialog.querySelector("[data-document-attach-kind]");
    const noteInput = dialog.querySelector("[data-document-attach-note]");
    const title = dialog.querySelector("[data-document-attach-title]");
    const description = dialog.querySelector("[data-document-attach-description]");
    const submitButton = dialog.querySelector("[data-document-attach-submit]");

    if (!(form instanceof HTMLFormElement) || !(kindInput instanceof HTMLSelectElement)) return;

    if (dialog.dataset.documentAttachModalBound !== "true") {
      dialog.dataset.documentAttachModalBound = "true";

      dialog.querySelectorAll("[data-document-attach-close]").forEach((button) => {
        button.addEventListener("click", () => dialog.close());
      });

      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
      });
    }

    document.querySelectorAll(`[data-document-attach-open][data-document-attach-modal-id="${dialog.id}"]`).forEach((button) => {
      if (!(button instanceof HTMLButtonElement) || button.dataset.documentAttachOpenBound === "true") return;

      button.dataset.documentAttachOpenBound = "true";
      button.addEventListener("click", () => {
        form.action = button.dataset.documentAttachAction || "#";

        if (patientIdInput instanceof HTMLInputElement) {
          patientIdInput.value = button.dataset.documentAttachPatientId || "";
        }

        if (returnToInput instanceof HTMLInputElement) {
          returnToInput.value = button.dataset.documentAttachReturnTo || "";
        }

        if (anchorInput instanceof HTMLInputElement) {
          anchorInput.value = button.dataset.documentAttachAnchor || "";
        }

        kindInput.value = button.dataset.documentAttachDefaultKind || "";

        if (noteInput instanceof HTMLTextAreaElement) {
          noteInput.value = "";
        }

        if (title instanceof HTMLElement) {
          const patientName = button.dataset.documentAttachPatientName || "patient";
          title.textContent = `Attach to ${patientName}`;
        }

        if (description instanceof HTMLElement) {
          const documentName = button.dataset.documentAttachDocumentName || "this file";
          description.textContent = `Classify ${documentName} and add an optional note for this patient.`;
        }

        if (submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement) {
          const patientFirstName = (button.textContent || "patient").replace(/^Attach to\s+/, "").trim();
          submitButton.value = `Attach to ${patientFirstName}`;
          submitButton.textContent = `Attach to ${patientFirstName}`;
        }

        dialog.showModal();
      });
    });
  });
}

function teardownDocumentAttachModals() {
  const dialogs = document.querySelectorAll("[data-document-attach-modal]");

  dialogs.forEach((dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (dialog.open) dialog.close();
    delete dialog.dataset.documentAttachModalBound;
  });

  const buttons = document.querySelectorAll("[data-document-attach-open]");
  buttons.forEach((button) => {
    delete button.dataset.documentAttachOpenBound;
  });
}

function syncCommunicationLoadingWarnings() {
  const scopes = document.querySelectorAll("[data-communication-loading-scope]");
  if (scopes.length === 0) return;

  scopes.forEach((scope) => {
    const warning = scope.querySelector("[data-communication-loading-warning]");
    if (!warning) return;

    const loadingPreviewCount = scope.querySelectorAll("[data-gmail-preview-url]").length;
    if (loadingPreviewCount === 0) {
      warning.remove();
    }
  });
}

const GMAIL_PREVIEW_MAX_CONCURRENT = 6;
let gmailPreviewActiveRequestCount = 0;

function loadGmailCommunicationPreview(container, url) {
  gmailPreviewActiveRequestCount += 1;

  fetch(url, {
    headers: {
      Accept: "application/json",
    },
  })
    .then((response) => response.json())
    .then((payload) => {
      if (typeof payload.html !== "string" || payload.html.length === 0) {
        throw new Error("Missing Gmail preview HTML");
      }

      container.innerHTML = payload.html;
      delete container.dataset.gmailPreviewUrl;
      initExpandableText();
      initEmailThreadOriginalToggles();
    })
    .catch(() => {
      container.innerHTML =
        '<p class="text-sm leading-6 text-neutral-600 dark:text-neutral-300">Unable to load email body.</p>';
      delete container.dataset.gmailPreviewUrl;
    })
    .finally(() => {
      gmailPreviewActiveRequestCount = Math.max(
        gmailPreviewActiveRequestCount - 1,
        0,
      );
      syncCommunicationLoadingWarnings();
      initGmailCommunicationPreviews();
    });
}

function initGmailCommunicationPreviews() {
  const containers = Array.from(document.querySelectorAll("[data-gmail-preview-url]"));
  if (containers.length === 0) {
    syncCommunicationLoadingWarnings();
    return;
  }

  const availableSlots = Math.max(
    GMAIL_PREVIEW_MAX_CONCURRENT - gmailPreviewActiveRequestCount,
    0,
  );
  if (availableSlots === 0) {
    syncCommunicationLoadingWarnings();
    return;
  }

  // Previews are rendered newest-first, so document order gives us the right priority.
  containers
    .filter((container) => container.dataset.gmailPreviewBound !== "true")
    .slice(0, availableSlots)
    .forEach((container) => {
    const url = container.dataset.gmailPreviewUrl;
    if (!url) return;

    container.dataset.gmailPreviewBound = "true";
    loadGmailCommunicationPreview(container, url);
  });
}

function teardownGmailCommunicationPreviews() {
  const containers = document.querySelectorAll("[data-gmail-preview-url]");

  containers.forEach((container) => {
    delete container.dataset.gmailPreviewBound;
  });
}

function initEmailReplyDrafts() {
  const triggers = document.querySelectorAll("[data-email-reply-draft-trigger='true']");
  if (triggers.length === 0) return;

  triggers.forEach((trigger) => {
    if (!(trigger instanceof HTMLButtonElement) || trigger.dataset.emailReplyDraftBound === "true") return;

    const url = trigger.dataset.emailReplyDraftUrl;
    if (!url) return;

    const container = trigger.closest("[data-email-detail-layout]")?.querySelector("[data-email-reply-draft-container='true']");
    if (!(container instanceof HTMLElement)) return;

    trigger.dataset.emailReplyDraftBound = "true";

    trigger.addEventListener("click", () => {
      if (trigger.disabled) return;

      const originalLabel = trigger.textContent;
      const loadingText = trigger.dataset.emailReplyDraftLoadingText || "Generating draft...";

      trigger.disabled = true;
      trigger.textContent = loadingText;

      fetch(url, {
        headers: {
          Accept: "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error("Unable to generate reply draft");
          return response.json();
        })
        .then((payload) => {
          if (typeof payload.html !== "string" || payload.html.length === 0) {
            throw new Error("Missing reply draft HTML");
          }

          container.innerHTML = payload.html;
        })
        .catch(() => {
          container.insertAdjacentHTML(
            "afterbegin",
            '<div class="rounded-lg border border-rose-200/80 bg-rose-50/80 p-3 dark:border-rose-900/60 dark:bg-rose-950/30"><p class="text-sm font-medium text-rose-800 dark:text-rose-200">Unable to generate an AI reply draft.</p></div>',
          );
        })
        .finally(() => {
          trigger.disabled = false;
          trigger.textContent = originalLabel || "Generate draft";
        });
    });
  });
}

function teardownEmailReplyDrafts() {
  const triggers = document.querySelectorAll("[data-email-reply-draft-trigger='true']");

  triggers.forEach((trigger) => {
    delete trigger.dataset.emailReplyDraftBound;
  });
}

function initConsultNoteAssessmentPlanSync() {
  const forms = document.querySelectorAll("form[data-consult-note-assessment-sync='true']");
  if (forms.length === 0) return;

  const appendUniquePlanText = (baseText, addition) => {
    const normalizedBaseText = (baseText || "").trim();
    const normalizedAddition = (addition || "").trim();

    if (!normalizedAddition || normalizedBaseText.includes(normalizedAddition)) return normalizedBaseText;
    return normalizedBaseText ? `${normalizedBaseText}\n\n${normalizedAddition}` : normalizedAddition;
  };

  forms.forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.consultNoteAssessmentSyncBound === "true") return;

    const planField = form.querySelector("#consult-note-section-plan");
    if (!(planField instanceof HTMLTextAreaElement)) return;

    const checkboxes = Array.from(
      form.querySelectorAll("input[type='checkbox'][data-consult-note-assessment-option='true']"),
    ).filter((checkbox) => checkbox instanceof HTMLInputElement);

    if (checkboxes.length === 0) return;

    form.dataset.consultNoteAssessmentSyncBound = "true";

    checkboxes.forEach((checkbox) => {
      checkbox.dataset.consultNoteAssessmentSelected = checkbox.checked ? "true" : "false";

      checkbox.addEventListener("change", () => {
        const wasSelected = checkbox.dataset.consultNoteAssessmentSelected === "true";
        checkbox.dataset.consultNoteAssessmentSelected = checkbox.checked ? "true" : "false";

        if (!checkbox.checked || wasSelected) return;

        const nextValue = appendUniquePlanText(planField.value, checkbox.dataset.planTemplate);
        if (nextValue === planField.value.trim()) return;

        planField.value = nextValue;
        planField.dispatchEvent(new Event("input", { bubbles: true }));
      });
    });
  });
}

const CONSULT_NOTE_UNSAVED_CHANGES_MESSAGE = "You have unsaved consult note changes. Leave without saving?";

function serializeFormState(form) {
  const entries = [];
  const formData = new FormData(form);

  formData.forEach((value, key) => {
    entries.push([key, value instanceof File ? value.name : String(value)]);
  });

  return JSON.stringify(entries);
}

function initConsultNoteNavigationGuards() {
  const forms = document.querySelectorAll("form[data-consult-note-navigation-guard='true']");
  if (forms.length === 0) return;

  forms.forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.consultNoteNavigationGuardBound === "true") return;

    form.dataset.consultNoteNavigationGuardBound = "true";

    let initialState = serializeFormState(form);
    let hasUnsavedChanges = false;
    let navigationConfirmed = false;

    const syncDirtyState = () => {
      hasUnsavedChanges = serializeFormState(form) !== initialState;
      if (hasUnsavedChanges) navigationConfirmed = false;
    };

    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges || navigationConfirmed) return;

      event.preventDefault();
      event.returnValue = "";
    };

    const handleBeforeVisit = (event) => {
      if (!hasUnsavedChanges || navigationConfirmed) return;

      const nextUrl = event.detail?.url;
      if (!nextUrl) return;

      event.preventDefault();
      if (!window.confirm(CONSULT_NOTE_UNSAVED_CHANGES_MESSAGE)) return;

      navigationConfirmed = true;
      hasUnsavedChanges = false;
      window.location.assign(nextUrl);
    };

    const handleSubmit = () => {
      navigationConfirmed = true;
      hasUnsavedChanges = false;
      initialState = serializeFormState(form);
    };

    form.addEventListener("input", syncDirtyState);
    form.addEventListener("change", syncDirtyState);
    form.addEventListener("submit", handleSubmit);
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("turbo:before-visit", handleBeforeVisit);

    form.__consultNoteNavigationGuardCleanup = () => {
      form.removeEventListener("input", syncDirtyState);
      form.removeEventListener("change", syncDirtyState);
      form.removeEventListener("submit", handleSubmit);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("turbo:before-visit", handleBeforeVisit);
      delete form.dataset.consultNoteNavigationGuardBound;
      delete form.__consultNoteNavigationGuardCleanup;
    };
  });
}

function teardownConsultNoteNavigationGuards() {
  const forms = document.querySelectorAll("form[data-consult-note-navigation-guard='true']");

  forms.forEach((form) => {
    form.__consultNoteNavigationGuardCleanup?.();
  });
}

function initBugReporter() {
  const element = document.querySelector("[data-bug-reporter-mount]");
  if (!element || element.__bugReporterCleanup) return;
  element.__bugReporterCleanup = mountSwpBugReporter(element);
  document.querySelectorAll("[data-report-bug]").forEach((button) => {
    button.onclick = () => {
      document.querySelector("[data-sidebar-close]")?.click();
      window.setTimeout(() => document.dispatchEvent(new Event("swp:report-bug")), 0);
    };
  });
}

function initApplicationShell() {
  initTaskDelivery();
  initTaskWorkspace();
  initDocumentPreviewPolling();
  initBugReporter();
  syncBrowserTimeZoneCookie();
  applyTheme(currentTheme());
  initThemeToggle();
  initMobileSidebar();
  initDismissibleDetails();
  initOpenInWindowLinks();
  initAdvancedSearchToggles();
  initDebouncedAutoSubmitForms();
  initFlashDismissButtons();
  initCopyButtons();
  initOperationNoteCopyButtons();
  initTaskStatusForms();
  initSlotTypeFilters();
  initSchedulingInviteRangeLinks();
  initSelectablePillGroups();
  initOrderSlipForms();
  initSchedulingChangeNotificationPreviews();
  initConfirmationPrompts();
  initCalendarScrollForms();
  initCalendarDayToggles();
  initExpandableText();
  initEmailThreadOriginalToggles();
  initDocumentAttachModals();
  initGmailCommunicationPreviews();
  initEmailReplyDrafts();
  initConsultNoteWorkspace();
  initConsultSchedulingInviteWorkspace();
  initOperationSchedulingInviteWorkspace();
  initConsultNoteAssessmentPlanSync();
  initConsultNoteNavigationGuards();
  initDocumentAiWorkspace();
  initDocumentAiMarkdownPreviews();
  initTaskRunComparisons();
  initThinkingLevelForms();
  initFaxExtractionPrompt();
  initDocumentComparePageLinks();
  initPatientAssignment();
  initPatientDetailsEditor();
  initBookingSheetWorkspace();
  document.querySelectorAll("[data-patient-packet-workspace]").forEach((element) => {
    if (!element.__reactRoot) element.__reactRoot = createRoot(element);
    element.__reactRoot.render(React.createElement(PatientPacketWorkspace, JSON.parse(element.dataset.props || "{}")));
  });
  initDesktopPatientSearch();
  initPatientPicker();
  initFaceSheetWorkspace();
  initFaceSheetValues();
  initOrderSlipCalibration();
  initPatientMergeWorkspace();
  initLabResultGraphTooltips();
  syncCommunicationLoadingWarnings();
}

function initThinkingLevelForms() {
  document.querySelectorAll("[data-thinking-level-form]").forEach((form) => {
    if (form.dataset.thinkingLevelBound === "true") return;

    const modelSelect = form.querySelector("[data-thinking-model-select]");
    const levelSelect = form.querySelector("[data-thinking-level-select]");
    if (!(modelSelect instanceof HTMLSelectElement) || !(levelSelect instanceof HTMLSelectElement)) return;

    let levelsByModel = {};
    try {
      levelsByModel = JSON.parse(levelSelect.dataset.levelsByModel || "{}");
    } catch (_error) {
      levelsByModel = {};
    }

    const syncLevelOptions = () => {
      const supportedLevels = levelsByModel[modelSelect.value] || [];

      Array.from(levelSelect.options).forEach((option) => {
        if (option.value === "") {
          option.disabled = false;
          option.hidden = false;
          return;
        }

        const supported = supportedLevels.includes(option.value);
        option.disabled = !supported;
        option.hidden = !supported;
      });

      if (levelSelect.value !== "" && !supportedLevels.includes(levelSelect.value)) {
        levelSelect.value = "";
      }
    };

    modelSelect.addEventListener("change", syncLevelOptions);
    syncLevelOptions();
    form.dataset.thinkingLevelBound = "true";
    form.__thinkingLevelCleanup = () => {
      modelSelect.removeEventListener("change", syncLevelOptions);
      delete form.dataset.thinkingLevelBound;
      delete form.__thinkingLevelCleanup;
    };
  });
}

function teardownThinkingLevelForms() {
  document.querySelectorAll("[data-thinking-level-form]").forEach((form) => {
    form.__thinkingLevelCleanup?.();
  });
}

function teardownDebouncedAutoSubmitForms() {
  const forms = document.querySelectorAll("form[data-debounced-auto-submit='true']");

  forms.forEach((form) => {
    form.__debouncedAutoSubmitCleanup?.();
  });
}

function initSchedulingChangeNotificationPreviews() {
  const forms = document.querySelectorAll("form[data-scheduling-change-notification-form='true']");

  forms.forEach((form) => {
    if (form.dataset.schedulingChangeNotificationPreviewBound === "true") return;

    const recipient = form.querySelector("[data-scheduling-change-notification-recipient]");
    const subject = form.querySelector("[data-scheduling-change-notification-subject]");
    const body = form.querySelector("[data-scheduling-change-notification-body]");
    const previewRecipient = document.querySelector("[data-scheduling-change-notification-preview-recipient]");
    const previewSubject = document.querySelector("[data-scheduling-change-notification-preview-subject]");
    const previewBody = document.querySelector("[data-scheduling-change-notification-preview-body]");

    const syncPreview = () => {
      if (previewRecipient) previewRecipient.textContent = recipient?.value || "";
      if (previewSubject) previewSubject.textContent = subject?.value || "";
      if (previewBody) previewBody.textContent = body?.value || "";
    };

    [recipient, subject, body].forEach((field) => field?.addEventListener("input", syncPreview));
    form.dataset.schedulingChangeNotificationPreviewBound = "true";
    form.__schedulingChangeNotificationPreviewCleanup = () => {
      [recipient, subject, body].forEach((field) => field?.removeEventListener("input", syncPreview));
      delete form.dataset.schedulingChangeNotificationPreviewBound;
      delete form.__schedulingChangeNotificationPreviewCleanup;
    };
    syncPreview();
  });
}

function teardownSchedulingChangeNotificationPreviews() {
  document.querySelectorAll("form[data-scheduling-change-notification-form='true']").forEach((form) => {
    form.__schedulingChangeNotificationPreviewCleanup?.();
  });
}

function initFaxExtractionPrompt() {
  const mounts = document.querySelectorAll("[data-fax-extraction-prompt]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(FaxExtractionPrompt, props));
  });
}

function initConsultNoteWorkspace() {
  const mounts = document.querySelectorAll("[data-consult-note-workspace]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(ConsultNoteWorkspace, props));
  });
}

function initConsultSchedulingInviteWorkspace() {
  const mounts = document.querySelectorAll("[data-consult-scheduling-invite-workspace]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(ConsultSchedulingInviteWorkspace, props));
  });
}

function initOperationSchedulingInviteWorkspace() {
  const mounts = document.querySelectorAll("[data-operation-scheduling-invite-workspace]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(OperationSchedulingInviteWorkspace, props));
  });
}

function initDocumentAiWorkspace() {
  const mounts = document.querySelectorAll("[data-document-ai-workspace]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(DocumentAiWorkspace, props));
  });
}

function teardownConsultNoteWorkspace() {
  const mounts = document.querySelectorAll("[data-consult-note-workspace]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function teardownConsultSchedulingInviteWorkspace() {
  const mounts = document.querySelectorAll("[data-consult-scheduling-invite-workspace]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function teardownOperationSchedulingInviteWorkspace() {
  const mounts = document.querySelectorAll("[data-operation-scheduling-invite-workspace]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function teardownDocumentAiWorkspace() {
  const mounts = document.querySelectorAll("[data-document-ai-workspace]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function initDocumentAiMarkdownPreviews() {
  const containers = document.querySelectorAll("[data-document-ai-markdown-preview]");
  if (containers.length === 0) return;

  containers.forEach((container) => {
    if (container.dataset.documentAiMarkdownPreviewBound === "true") return;

    const rawButton = container.querySelector("[data-document-ai-markdown-toggle='raw']");
    const markdownButton = container.querySelector("[data-document-ai-markdown-toggle='markdown']");
    const rawPane = container.querySelector("[data-document-ai-markdown-pane='raw']");
    const markdownPane = container.querySelector("[data-document-ai-markdown-pane='markdown']");

    if (!(rawButton instanceof HTMLButtonElement)) return;
    if (!(markdownButton instanceof HTMLButtonElement)) return;
    if (!(rawPane instanceof HTMLElement)) return;
    if (!(markdownPane instanceof HTMLElement)) return;

    container.dataset.documentAiMarkdownPreviewBound = "true";
    const rawText = rawPane.textContent || "";
    let markdownRendered = false;

    const applyToggleClasses = (button, active) => {
      button.classList.add("app-pill");
      button.classList.toggle("app-pill-active", active);
      button.classList.toggle("app-pill-inactive", !active);
      button.setAttribute("aria-pressed", active.toString());
    };

    const renderMarkdown = () => {
      if (markdownRendered) return;

      const renderedHtml = marked.parse(rawText, { breaks: true, gfm: true });
      markdownPane.innerHTML = DOMPurify.sanitize(renderedHtml);
      markdownRendered = true;
    };

    const setMode = (mode) => {
      const markdownMode = mode === "markdown";
      if (markdownMode) renderMarkdown();

      rawPane.classList.toggle("hidden", markdownMode);
      markdownPane.classList.toggle("hidden", !markdownMode);
      applyToggleClasses(rawButton, !markdownMode);
      applyToggleClasses(markdownButton, markdownMode);
    };

    const handleRawClick = () => setMode("raw");
    const handleMarkdownClick = () => setMode("markdown");

    rawButton.addEventListener("click", handleRawClick);
    markdownButton.addEventListener("click", handleMarkdownClick);

    container.__documentAiMarkdownPreviewCleanup = () => {
      rawButton.removeEventListener("click", handleRawClick);
      markdownButton.removeEventListener("click", handleMarkdownClick);
      delete container.dataset.documentAiMarkdownPreviewBound;
      delete container.__documentAiMarkdownPreviewCleanup;
    };

    setMode("raw");
  });
}

function teardownFaxExtractionPrompt() {
  const mounts = document.querySelectorAll("[data-fax-extraction-prompt]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function teardownDocumentAiMarkdownPreviews() {
  const containers = document.querySelectorAll("[data-document-ai-markdown-preview]");

  containers.forEach((container) => {
    container.__documentAiMarkdownPreviewCleanup?.();
  });
}

function initTaskRunComparisons() {
  const comparisons = document.querySelectorAll("[data-task-run-comparison]");

  comparisons.forEach((comparison) => {
    if (comparison.dataset.taskRunComparisonBound === "true") return;

    const buttons = Array.from(comparison.querySelectorAll("[data-task-run-comparison-filter]"));
    const groups = Array.from(comparison.querySelectorAll("[data-task-run-comparison-group]"));
    const emptyState = comparison.querySelector("[data-task-run-comparison-empty]");

    const rowMatchesFilter = (row, filter) => {
      if (filter === "all") return true;
      if (filter === "important") return row.dataset.classification === "substantive";
      if (filter === "candidate") return row.dataset.kind === "added";
      if (filter === "baseline") return row.dataset.kind === "removed";
      if (filter === "modified") return row.dataset.kind === "changed";
      if (filter === "review") return row.dataset.approximate === "true";
      if (filter === "noise") return ["formatting", "provenance"].includes(row.dataset.classification);

      return true;
    };

    const syncButton = (button, active) => {
      button.classList.toggle("app-pill-active", active);
      button.classList.toggle("app-pill-inactive", !active);
      button.setAttribute("aria-pressed", active.toString());

      const count = button.querySelector(".app-pill-count");
      count?.classList.toggle("bg-white/20", active);
      count?.classList.toggle("bg-neutral-100", !active);
      count?.classList.toggle("dark:bg-neutral-800", !active);
    };

    const applyFilter = (filter) => {
      let totalVisible = 0;

      groups.forEach((group) => {
        const rows = Array.from(group.querySelectorAll("[data-task-run-comparison-row]"));
        let groupVisible = 0;

        rows.forEach((row) => {
          const visible = rowMatchesFilter(row, filter);
          row.classList.toggle("hidden", !visible);
          if (visible) groupVisible += 1;
        });

        group.classList.toggle("hidden", groupVisible === 0);
        const visibleCount = group.querySelector("[data-task-run-comparison-visible-count]");
        if (visibleCount) {
          visibleCount.textContent = `${groupVisible} visible ${groupVisible === 1 ? "difference" : "differences"}`;
        }
        totalVisible += groupVisible;
      });

      buttons.forEach((button) => syncButton(button, button.dataset.taskRunComparisonFilter === filter));
      emptyState?.classList.toggle("hidden", totalVisible !== 0);
    };

    const handlers = buttons.map((button) => {
      const handler = () => applyFilter(button.dataset.taskRunComparisonFilter || "all");
      button.addEventListener("click", handler);
      return [button, handler];
    });

    comparison.dataset.taskRunComparisonBound = "true";
    comparison.__taskRunComparisonCleanup = () => {
      handlers.forEach(([button, handler]) => button.removeEventListener("click", handler));
      delete comparison.dataset.taskRunComparisonBound;
      delete comparison.__taskRunComparisonCleanup;
    };

    applyFilter("important");
  });
}

function teardownTaskRunComparisons() {
  document.querySelectorAll("[data-task-run-comparison]").forEach((comparison) => {
    comparison.__taskRunComparisonCleanup?.();
  });
}

function initDocumentComparePageLinks() {
  const containers = document.querySelectorAll("[data-document-compare]");
  if (containers.length === 0) return;

  const normalizeCompareValue = (value) => (value || "").trim().replace(/\s+/g, " ").toLowerCase();

  const setHighlightedRow = (container, row) => {
    const existingRows = container.querySelectorAll("[data-document-compare-result-row='active']");
    existingRows.forEach((existingRow) => {
      existingRow.dataset.documentCompareResultRow = "true";
      existingRow.querySelectorAll("td").forEach((cell) => {
        cell.classList.remove("bg-amber-100/80", "dark:bg-amber-900/30");
      });
    });

    if (!(row instanceof HTMLTableRowElement)) return;

    row.dataset.documentCompareResultRow = "active";
    row.querySelectorAll("td").forEach((cell) => {
      cell.classList.add("bg-amber-100/80", "dark:bg-amber-900/30");
    });
    row.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const findHighlightedRow = (container) => {
    const params = new URLSearchParams(window.location.search);
    const fieldKey = normalizeCompareValue(params.get("field_key"));
    const resultDate = normalizeCompareValue(params.get("result_date"));
    const resultValue = normalizeCompareValue(params.get("result_value"));
    const sourcePage = normalizeCompareValue(params.get("page"));
    const rows = Array.from(container.querySelectorAll("[data-document-compare-result-row]"));

    if (rows.length === 0) return null;

    const scoredRows = rows.map((row) => {
      const rowFieldKey = normalizeCompareValue(row.getAttribute("data-result-field-key"));
      const rowDate = normalizeCompareValue(row.getAttribute("data-result-date"));
      const rowValue = normalizeCompareValue(row.getAttribute("data-result-value"));
      const rowPage = normalizeCompareValue(row.getAttribute("data-source-page"));
      let score = 0;

      if (fieldKey && rowFieldKey === fieldKey) score += 4;
      if (resultDate && rowDate === resultDate) score += 2;
      if (resultValue && rowValue === resultValue) score += 3;
      if (sourcePage && rowPage === sourcePage) score += 1;

      return { row, score };
    });

    const bestMatch = scoredRows.sort((a, b) => b.score - a.score)[0];
    return bestMatch && bestMatch.score > 0 ? bestMatch.row : null;
  };

  containers.forEach((container) => {
    if (container.dataset.documentCompareBound === "true") return;

    container.dataset.documentCompareBound = "true";
    setHighlightedRow(container, findHighlightedRow(container));
    container.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest("[data-document-compare-page-link]");
      if (!(button instanceof HTMLButtonElement)) return;

      const frame = container.querySelector("[data-document-compare-frame='true']");
      if (!(frame instanceof HTMLIFrameElement)) return;

      const page = Number(button.dataset.page);
      if (!Number.isInteger(page) || page < 1) return;

      const currentSrc = frame.getAttribute("src") || "";
      const [baseSrc, currentFragment = ""] = currentSrc.split("#");
      const fragmentParams = new URLSearchParams(currentFragment);

      fragmentParams.set("page", String(page));

      const nextFragment = fragmentParams.toString();
      frame.setAttribute("src", nextFragment ? `${baseSrc}#${nextFragment}` : baseSrc);
      frame.scrollIntoView({ behavior: "smooth", block: "start" });
      setHighlightedRow(container, button.closest("tr"));
    });
  });
}

function teardownDocumentComparePageLinks() {
  const containers = document.querySelectorAll("[data-document-compare]");

  containers.forEach((container) => {
    delete container.dataset.documentCompareBound;
  });
}

function initPatientAssignment() {
  const mounts = document.querySelectorAll("[data-patient-assignment]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(FaxPatientAssignment, props));
  });
}

function initBookingSheetWorkspace() {
  document.querySelectorAll("[data-booking-sheet-workspace]").forEach((element) => {
    if (!element.__reactRoot) element.__reactRoot = createRoot(element);
    element.__reactRoot.render(React.createElement(BookingSheetWorkspace, JSON.parse(element.dataset.props || "{}")));
  });
}

function initPatientDetailsEditor() {
  const mounts = document.querySelectorAll("[data-patient-details-editor]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(PatientDetailsEditor, props));
  });
}

function initPatientPicker() {
  const mounts = document.querySelectorAll("[data-patient-picker]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(PatientPicker, props));
  });
}

function initDesktopPatientSearch() {
  const mounts = document.querySelectorAll("[data-patient-navbar-search]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(DesktopPatientSearch, props));
  });
}

function teardownPatientAssignment() {
  const mounts = document.querySelectorAll("[data-patient-assignment]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function teardownPatientDetailsEditor() {
  const mounts = document.querySelectorAll("[data-patient-details-editor]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function teardownPatientPicker() {
  const mounts = document.querySelectorAll("[data-patient-picker]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function teardownDesktopPatientSearch() {
  const mounts = document.querySelectorAll("[data-patient-navbar-search]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function initFaceSheetWorkspace() {
  const mounts = document.querySelectorAll("[data-face-sheet-workspace]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(FaceSheetWorkspace, props));
  });
}

function teardownFaceSheetWorkspace() {
  const mounts = document.querySelectorAll("[data-face-sheet-workspace]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function initFaceSheetValues() {
  const mounts = document.querySelectorAll("[data-face-sheet-values]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(FaceSheetValues, props));
  });
}

function teardownFaceSheetValues() {
  const mounts = document.querySelectorAll("[data-face-sheet-values]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function initPatientMergeWorkspace() {
  const mounts = document.querySelectorAll("[data-patient-merge-workspace]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(PatientMergeWorkspace, props));
  });
}

function initOrderSlipCalibration() {
  const mounts = document.querySelectorAll("[data-order-slip-calibration]");
  if (mounts.length === 0) return;

  mounts.forEach((element) => {
    const props = JSON.parse(element.dataset.props || "{}");

    if (!element.__reactRoot) {
      element.__reactRoot = createRoot(element);
    }

    element.__reactRoot.render(React.createElement(OrderSlipCalibration, props));
  });
}

function initOrderSlipForms() {
  const forms = document.querySelectorAll("[data-order-slip-form]");
  if (forms.length === 0) return;

  forms.forEach((form) => {
    form.__orderSlipCleanup?.();

    const templateSelect = form.querySelector("[data-order-slip-template-select]");
    if (!(templateSelect instanceof HTMLSelectElement)) return;

    const selectedOption = () => templateSelect.options[templateSelect.selectedIndex];
    const cleanupLabLines = initOrderSlipLabLines(form);
    const diagnosisSelect = form.querySelector("[data-order-slip-diagnosis-select]");
    const customDiagnosisOption = diagnosisSelect?.querySelector("[data-order-slip-custom-diagnosis-option]");
    const customDiagnosisSection = form.querySelector("[data-order-slip-custom-diagnosis]");
    const customDiagnosisInput = customDiagnosisSection?.querySelector("input");

    const defaultPayload = () => {
      try {
        return JSON.parse(selectedOption()?.dataset.defaultPayload || "{}");
      } catch (_error) {
        return {};
      }
    };

    const updateSections = () => {
      const option = selectedOption();
      const templateKey = templateSelect.value;
      const category = option?.dataset.category || "";

      form.querySelectorAll("[data-order-slip-section]").forEach((section) => {
        const templateKeys = (section.dataset.templateKeys || "").split(/\s+/).filter(Boolean);
        const categories = (section.dataset.templateCategories || "").split(/\s+/).filter(Boolean);
        const matchesTemplate = templateKeys.length > 0 && templateKeys.includes(templateKey);
        const matchesCategory = categories.length > 0 && categories.includes(category);
        const visible = matchesTemplate || matchesCategory || (templateKeys.length === 0 && categories.length === 0);

        section.classList.toggle("hidden", !visible);
        section.querySelectorAll("input, select, textarea").forEach((input) => {
          input.disabled = !visible;
        });
      });
    };

    const updateDiagnosisControls = () => {
      if (!(diagnosisSelect instanceof HTMLSelectElement)) return;

      const customDiagnosisSupported = selectedOption()?.dataset.customDiagnosis === "true";
      const customDiagnosisSelected = customDiagnosisSupported && diagnosisSelect.value === "custom";
      const showCustomDiagnosis = !customDiagnosisSupported || customDiagnosisSelected;

      if (customDiagnosisOption instanceof HTMLOptionElement) {
        customDiagnosisOption.disabled = !customDiagnosisSupported;
        customDiagnosisOption.hidden = !customDiagnosisSupported;
      }

      customDiagnosisSection?.classList.toggle("hidden", !showCustomDiagnosis);
      customDiagnosisSection?.setAttribute("aria-hidden", (!showCustomDiagnosis).toString());

      if (customDiagnosisInput instanceof HTMLInputElement) {
        customDiagnosisInput.disabled = !showCustomDiagnosis;
        customDiagnosisInput.required = customDiagnosisSelected;
      }
    };

    const applyDefaults = () => {
      const payload = defaultPayload();

      form.querySelectorAll("[data-order-slip-payload-field]").forEach((field) => {
        const key = field.dataset.orderSlipPayloadField;
        if (!key || !(key in payload)) return;

        field.value = payload[key] || "";
      });

      const requestedLabKeys = Array.isArray(payload.requested_lab_keys) ? payload.requested_lab_keys : [];
      form.querySelectorAll("input[type='checkbox'][data-order-slip-payload-array='requested_lab_keys']").forEach((checkbox) => {
        checkbox.checked = requestedLabKeys.includes(checkbox.value);
      });

      const customLabLines = Array.isArray(payload.custom_lab_lines) ? payload.custom_lab_lines : [];
      form.querySelectorAll("[data-order-slip-payload-array-text='custom_lab_lines']").forEach((field) => {
        const index = Number(field.dataset.orderSlipArrayIndex || 0);
        field.value = customLabLines[index] || "";
      });
    };

    const requestedLabCheckboxes = Array.from(
      form.querySelectorAll("input[type='checkbox'][data-order-slip-payload-array='requested_lab_keys']")
    );

    const clearRequestedLabValidity = () => {
      requestedLabCheckboxes.forEach((checkbox) => checkbox.setCustomValidity(""));
    };

    const validateRequestedLabs = () => {
      const enabledCheckboxes = requestedLabCheckboxes.filter((checkbox) => !checkbox.disabled);
      clearRequestedLabValidity();
      if (selectedOption()?.dataset.requiresLabs !== "true") return true;
      if (enabledCheckboxes.length === 0 || enabledCheckboxes.some((checkbox) => checkbox.checked)) return true;

      enabledCheckboxes[0].setCustomValidity("Select at least one lab.");
      return false;
    };

    const handleRequestedLabChange = () => {
      if (requestedLabCheckboxes.some((checkbox) => !checkbox.disabled && checkbox.checked)) {
        clearRequestedLabValidity();
      }
    };

    const handleSubmit = (event) => {
      if (validateRequestedLabs()) return;

      event.preventDefault();
      requestedLabCheckboxes.find((checkbox) => !checkbox.disabled)?.reportValidity();
    };

    const handleTemplateChange = () => {
      applyDefaults();
      updateSections();
      updateDiagnosisControls();
      clearRequestedLabValidity();
    };

    const deliveryModeSelect = form.querySelector("[data-order-slip-delivery-mode-select]");
    const delayFields = form.querySelector("[data-order-slip-delay-fields]");
    const dateFields = form.querySelector("[data-order-slip-date-fields]");
    const submitButton = form.querySelector("[data-order-slip-submit]");

    const updateDeliveryControls = () => {
      if (!(deliveryModeSelect instanceof HTMLSelectElement)) return;

      const sendLater = deliveryModeSelect.value === "send_later";
      const sendOnDate = deliveryModeSelect.value === "send_on_date";
      delayFields?.classList.toggle("hidden", !sendLater);
      delayFields?.setAttribute("aria-hidden", (!sendLater).toString());
      delayFields?.querySelectorAll("input, select").forEach((input) => {
        input.disabled = !sendLater;
      });
      dateFields?.classList.toggle("hidden", !sendOnDate);
      dateFields?.setAttribute("aria-hidden", (!sendOnDate).toString());
      dateFields?.querySelectorAll("input").forEach((input) => {
        input.disabled = !sendOnDate;
        input.required = sendOnDate;
      });

      if (submitButton instanceof HTMLButtonElement) {
        if (sendOnDate) {
          submitButton.textContent = submitButton.dataset.sendOnDateLabel || "Schedule order slip";
          submitButton.dataset.turboConfirm = submitButton.dataset.sendOnDateConfirm || "Schedule this order slip to send on the selected date?";
        } else if (sendLater) {
          submitButton.textContent = submitButton.dataset.sendLaterLabel || "Schedule order slip";
          submitButton.dataset.turboConfirm = submitButton.dataset.sendLaterConfirm || "Schedule this order slip to send later?";
        } else {
          submitButton.textContent = submitButton.dataset.sendNowLabel || "Send order slip";
          submitButton.dataset.turboConfirm = submitButton.dataset.sendNowConfirm || "";
        }
      }
    };

    templateSelect.addEventListener("change", handleTemplateChange);
    diagnosisSelect?.addEventListener("change", updateDiagnosisControls);
    deliveryModeSelect?.addEventListener("change", updateDeliveryControls);
    requestedLabCheckboxes.forEach((checkbox) => checkbox.addEventListener("change", handleRequestedLabChange));
    form.addEventListener("submit", handleSubmit);
    updateSections();
    updateDiagnosisControls();
    updateDeliveryControls();

    form.__orderSlipCleanup = () => {
      cleanupLabLines();
      templateSelect.removeEventListener("change", handleTemplateChange);
      diagnosisSelect?.removeEventListener("change", updateDiagnosisControls);
      deliveryModeSelect?.removeEventListener("change", updateDeliveryControls);
      requestedLabCheckboxes.forEach((checkbox) => checkbox.removeEventListener("change", handleRequestedLabChange));
      form.removeEventListener("submit", handleSubmit);
      delete form.__orderSlipCleanup;
    };
  });
}

function teardownOrderSlipForms() {
  document.querySelectorAll("[data-order-slip-form]").forEach((form) => {
    form.__orderSlipCleanup?.();
  });
}

function teardownPatientMergeWorkspace() {
  const mounts = document.querySelectorAll("[data-patient-merge-workspace]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function teardownOrderSlipCalibration() {
  const mounts = document.querySelectorAll("[data-order-slip-calibration]");

  mounts.forEach((element) => {
    if (!element.__reactRoot) return;

    element.__reactRoot.unmount();
    delete element.__reactRoot;
  });
}

function initLabResultGraphTooltips() {
  teardownLabResultGraphTooltips?.();

  const points = Array.from(document.querySelectorAll("[data-lab-result-graph-point='true']"));
  if (points.length === 0) return;

  const tooltip = document.createElement("div");
  tooltip.className =
    "pointer-events-none fixed z-50 hidden max-w-xs rounded-md bg-neutral-950 px-2 py-1.5 text-xs font-medium text-white shadow-lg ring-1 ring-black/10 dark:bg-white dark:text-neutral-950";
  tooltip.setAttribute("role", "tooltip");
  document.body.appendChild(tooltip);

  const positionTooltip = (event) => {
    const offset = 14;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipRect = tooltip.getBoundingClientRect();
    const maxLeft = Math.max(8, viewportWidth - tooltipRect.width - 8);
    const maxTop = Math.max(8, viewportHeight - tooltipRect.height - 8);
    const nextLeft = Math.min(event.clientX + offset, maxLeft);
    const nextTop = Math.min(event.clientY - tooltipRect.height - offset, maxTop);

    tooltip.style.left = `${Math.max(8, nextLeft)}px`;
    tooltip.style.top = `${Math.max(8, nextTop)}px`;
  };

  const showTooltip = (event) => {
    const point = event.currentTarget;
    if (!(point instanceof SVGElement)) return;

    const content = point.dataset.labResultGraphTooltip;
    if (!content) return;

    tooltip.textContent = content;
    tooltip.classList.remove("hidden");
    positionTooltip(event);
  };

  const hideTooltip = () => {
    tooltip.classList.add("hidden");
  };

  points.forEach((point) => {
    point.addEventListener("mouseenter", showTooltip);
    point.addEventListener("mousemove", positionTooltip);
    point.addEventListener("mouseleave", hideTooltip);
  });

  teardownLabResultGraphTooltips = () => {
    points.forEach((point) => {
      point.removeEventListener("mouseenter", showTooltip);
      point.removeEventListener("mousemove", positionTooltip);
      point.removeEventListener("mouseleave", hideTooltip);
    });

    tooltip.remove();
    teardownLabResultGraphTooltips = null;
  };
}

document.addEventListener("DOMContentLoaded", initApplicationShell);
document.addEventListener("turbo:load", initApplicationShell);
window.addEventListener("pagehide", teardownDocumentPreviewPolling);
window.addEventListener("pagehide", teardownTaskDelivery);
window.addEventListener("pagehide", teardownTaskWorkspace);
window.addEventListener("pageshow", initDocumentPreviewPolling);
window.addEventListener("pageshow", () => initTaskDelivery());
window.addEventListener("pageshow", () => initTaskWorkspace());
document.addEventListener("turbo:before-cache", () => {
  teardownTaskDelivery();
  teardownTaskWorkspace();
  teardownDocumentPreviewPolling();
  document.querySelectorAll("[data-bug-reporter-mount]").forEach((element) => {
    element.__bugReporterCleanup?.();
    delete element.__bugReporterCleanup;
  });
  teardownDebouncedAutoSubmitForms();
  teardownGmailCommunicationPreviews();
  teardownEmailReplyDrafts();
  teardownDocumentAttachModals();
  teardownConsultNoteWorkspace();
  teardownConsultSchedulingInviteWorkspace();
  teardownOperationSchedulingInviteWorkspace();
  teardownDocumentAiWorkspace();
  teardownDocumentAiMarkdownPreviews();
  teardownTaskRunComparisons();
  teardownThinkingLevelForms();
  teardownConsultNoteNavigationGuards();
  teardownFaxExtractionPrompt();
  teardownDocumentComparePageLinks();
  teardownPatientAssignment();
  teardownPatientDetailsEditor();
  document.querySelectorAll("[data-booking-sheet-workspace], [data-patient-packet-workspace]").forEach((element) => {
    element.__reactRoot?.unmount();
    delete element.__reactRoot;
  });
  teardownDesktopPatientSearch();
  teardownPatientPicker();
  teardownFaceSheetWorkspace();
  teardownFaceSheetValues();
  teardownOrderSlipForms();
  teardownSchedulingChangeNotificationPreviews();
  teardownPatientMergeWorkspace();
  teardownOrderSlipCalibration();
  teardownLabResultGraphTooltips?.();
});
